import {
  Button,
  Chip,
  cn,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  ScrollShadow,
} from '@heroui/react'
import { Dropdown } from '@heroui/react'
import {
  queryKeys,
  type QuotaSummaryDto,
  type TenantQuotaSummaryDto,
  useActivateUser,
  useDeleteUserInvitation,
  useGetUsers,
  useMyTenantDetails,
  type UserDataResponse,
  useResendUserInvitation,
  useSuspendUser,
  useTenant,
  useTenantQuotaSummary,
} from '@qse/api'
import { resetTenantIdHeader, updateTenantIdHeader } from '@qse/api'
import { type Column, ModelControlEnum, UserRoleEnum } from '@qse/constants'
import { useActiveModelStore, useBillingStatusStore, useUserStore } from '@qse/stores'
import { EmptyState, EmptyStateIcon, ErrorData, TableComponent, toast } from '@qse/ui'
import {
  bytesToGB,
  formatStorageUsage as formatStorageUsageUtil,
  formatStorageValue,
  truncateToDecimals,
  useIsMobile,
  usePageFocusRefetch,
  useURLParams,
} from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { ChevronDownIcon, XIcon } from 'lucide-react'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

const formatStorageUsage = (user: UserDataResponse & { quotaSummary?: any }) => {
  if (!user.quotaSummary?.storage) {
    return {
      used: 0,
      total: 0,
      percentage: 0,
      overQuota: false,
      displayText: 'N/A',
    }
  }

  const { used, quota, utilizationPercent } = user.quotaSummary.storage
  const usedGB = bytesToGB(used)
  const totalGB = bytesToGB(quota)

  // When quota is 0 and used > 0, user is over-quota
  const overQuota = quota === 0 ? used > 0 : used >= quota

  // Calculate percentage: if quota is 0 and used > 0, show 100% (over-quota)
  // Otherwise use utilizationPercent or calculate it
  let percentage: number

  if (quota === 0 && used > 0) {
    percentage = 100 // Show as over-quota
  } else if (quota === 0) {
    percentage = 0
  } else if (utilizationPercent !== undefined && utilizationPercent !== null) {
    percentage = truncateToDecimals(utilizationPercent)
  } else {
    percentage = truncateToDecimals((used / quota) * 100)
  }

  // Use new dynamic formatter utility for display text (used and quota are in bytes)
  const displayText = formatStorageUsageUtil(used, quota)

  return {
    used: usedGB,
    total: totalGB,
    percentage,
    overQuota,
    displayText,
  }
}

const formatApiUsage = (user: UserDataResponse & { quotaSummary?: any }) => {
  if (!user.quotaSummary?.service) {
    return {
      used: 0,
      total: 0,
      percentage: 0,
      displayText: 'N/A',
    }
  }

  const { used, quota, utilizationPercent } = user.quotaSummary.service

  return {
    used,
    total: quota || 0,
    percentage: truncateToDecimals(utilizationPercent || 0),
    displayText: `${used.toLocaleString()} / ${(quota || 0).toLocaleString()}`,
  }
}

interface UserCardProps {
  user: UserDataResponse & {
    storageQuota?: number | null
    apiQuota?: number | null
    status?: string
    quotaSummary?: QuotaSummaryDto
  }
  onView?: (id: string) => void
  onEdit: (user: UserDataResponse, action: string) => void
  onDelete: (id: string) => void
  onSuspend: (id: string) => void
  onActivate: (id: string) => void
  onResendInvitation: (id: string) => void
  onTransferAdmin: (user: UserDataResponse) => void
  currentUserId?: string
  currentUserRole?: string
  currentUserIsPrimary?: boolean
}

const columns: Column[] = [
  { name: 'User', uid: 'user' },
  { name: 'API Usage', uid: 'apiUsage' },
  { name: 'Storage Usage', uid: 'storageUsage' },
  { name: 'Status', uid: 'status' },
  { name: 'Last Active', uid: 'lastActive' },
  { name: 'QPA Assigned', uid: 'qpaSeedCount' },
  { name: '', uid: 'actions' },
]

const TenantHeaderSkeleton = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {/* Account Card Skeleton */}
      <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-3 lg:p-4 shadow animate-pulse">
        <div className="h-7 bg-gray-300 dark:bg-gray-600 rounded w-24 mb-6" />
        <div className="space-y-6">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-full" />
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4" />
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-2/3" />
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2" />
        </div>
      </div>

      {/* Admin Contact Card Skeleton */}
      <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-3 lg:p-4 shadow animate-pulse">
        <div className="h-7 bg-gray-300 dark:bg-gray-600 rounded w-32 mb-6" />
        <div className="space-y-6">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-full" />
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-5/6" />
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-2/3" />
        </div>
      </div>

      {/* Plan Details Card Skeleton */}
      <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-3 lg:p-4 shadow animate-pulse">
        <div className="h-7 bg-gray-300 dark:bg-gray-600 rounded w-28 mb-6" />
        <div className="space-y-6">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-full" />
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-4/5" />
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4" />
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-2/3" />
        </div>
      </div>
    </div>
  )
}

const UserCardSkeleton = () => {
  return (
    <div className="dark:bg-[#1A1A1A] bg-white rounded-lg border dark:border-[#2A2A2A] border-gray-200 p-4 shadow relative animate-pulse">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="h-5 bg-gray-300 dark:bg-gray-600 rounded w-32 mb-2" />
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-48" />
        </div>
        <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded" />
      </div>
      <div className="flex justify-between text-sm mt-4">
        <div className="flex-1">
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-20 mb-2" />
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24 mb-2" />
          <div className="w-full bg-gray-300 dark:bg-gray-600 rounded-full h-1 mb-1" />
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-16" />
        </div>
        <div className="ml-4">
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-16 mb-2" />
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-20" />
        </div>
      </div>
      <div className="flex justify-between text-xs mt-4">
        <div>
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-20 mb-1" />
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-16" />
        </div>
        <div className="w-16 h-6 bg-gray-300 dark:bg-gray-600 rounded" />
      </div>
    </div>
  )
}

const UserCard = ({
  user,
  onEdit,
  onDelete,
  onSuspend,
  onActivate,
  onResendInvitation,
  onTransferAdmin,
  currentUserId,
  currentUserRole,
  currentUserIsPrimary,
}: UserCardProps) => {
  const status = user.status || (user.isActive ? 'active' : 'disabled')

  // Only a super admin may suspend an admin. Tenant admins — primary or not — are limited to
  // suspending tenant users. Mirrors the guard in handleSuspendUser.
  const isSuperAdmin = currentUserRole === UserRoleEnum.SUPER_ADMIN
  const isTargetTenantAdmin = user.userRole === UserRoleEnum.TENANT_ADMIN
  const canSuspend =
    status === 'active' && user.id !== currentUserId && (isSuperAdmin || !isTargetTenantAdmin)

  return (
    <div className="dark:bg-[#1A1A1A] bg-white rounded-lg border dark:border-[#2A2A2A] border-gray-200 p-4 shadow relative">
      <div className="flex justify-between items-start">
        <div>
          <div className="font-medium text-base flex items-center gap-2">
            {user.firstName} {user.lastName}
            {user.isPrimary && (
              <Chip color="warning" size="sm" variant="flat">
                Primary Admin
              </Chip>
            )}
          </div>
          <div className="text-xs text-gray-600 dark:text-[#AFAFAF]">{user.email}</div>
        </div>
        <Dropdown
          classNames={{
            content: 'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
          }}
        >
          <DropdownTrigger>
            <Button isIconOnly size="sm" variant="light">
              <span className="text-2xl">⋮</span>
            </Button>
          </DropdownTrigger>
          <DropdownMenu>
            <DropdownItem key="edit" onPress={() => onEdit(user, 'edit')}>
              Edit User
            </DropdownItem>
            {canSuspend ? (
              <DropdownItem key="suspend" onPress={() => onSuspend(user.id)}>
                Suspend User
              </DropdownItem>
            ) : null}

            {status === 'suspended' ? (
              <DropdownItem key="enable" onPress={() => onActivate(user.id)}>
                Enable
              </DropdownItem>
            ) : null}

            {status !== 'active' && status !== 'suspended' && user.id !== currentUserId ? (
              <DropdownItem key="resend" onPress={() => onResendInvitation(user.id)}>
                Resend Invitation
              </DropdownItem>
            ) : null}

            {(currentUserIsPrimary || currentUserRole === UserRoleEnum.SUPER_ADMIN) &&
            user.userRole === UserRoleEnum.TENANT_ADMIN &&
            !user.isPrimary &&
            status === 'active' ? (
              <DropdownItem key="transfer" onPress={() => onTransferAdmin(user)}>
                Transfer Admin
              </DropdownItem>
            ) : null}

            {/* Delete User - Available for invited/suspended users, not for active users or current user */}
            {user.id !== currentUserId &&
            (status === 'invited' || status === 'suspended') &&
            (user.userRole !== UserRoleEnum.TENANT_ADMIN ||
              currentUserRole === UserRoleEnum.SUPER_ADMIN) &&
            !user.isPrimary ? (
              <DropdownItem key="delete" onPress={() => onDelete(user.id)}>
                Delete User
              </DropdownItem>
            ) : null}
          </DropdownMenu>
        </Dropdown>
      </div>
      <div className="flex justify-between text-sm mt-2">
        <div>
          <div className="font-light">Storage Usage</div>
          <div className="font-medium">{formatStorageUsage(user).displayText}</div>
          <div className="w-full bg-gray-200 rounded-full h-1 mt-1 relative overflow-visible">
            <div
              className={`${formatStorageUsage(user).overQuota ? 'bg-red-600' : 'bg-blue-600 dark:bg-blue-400'} h-1 rounded-full transition-all duration-300`}
              style={{ width: `${Math.min(formatStorageUsage(user).percentage, 100)}%` }}
            />
            {formatStorageUsage(user).percentage > 100 && (
              <div
                className={`${formatStorageUsage(user).overQuota ? 'bg-red-600' : 'bg-blue-600 dark:bg-blue-400'} h-1 rounded-full opacity-50 absolute top-0 right-0`}
                style={{ width: `${Math.min(formatStorageUsage(user).percentage - 100, 20)}%` }}
              />
            )}
          </div>
          <div className="text-xs text-gray-600 dark:text-[#AFAFAF] mt-1">
            {formatStorageUsage(user).total === 0 && formatStorageUsage(user).used > 0
              ? 'Over quota - No quota allocated'
              : `${formatStorageUsage(user).percentage}% used`}
          </div>
        </div>
        <div>
          <div className="font-light">API Usage</div>
          <div className="text-xs text-gray-600 dark:text-[#AFAFAF] mt-1">
            {formatApiUsage(user).displayText}
          </div>
        </div>
      </div>
      <div className="flex justify-between text-xs mt-2">
        <div>
          <div className="font-light">Created Date</div>
          <div>{new Date(user.createdAt).toLocaleDateString()}</div>
        </div>
        <Chip
          className="capitalize rounded font-medium"
          size="sm"
          variant="flat"
          style={{
            backgroundColor: status === 'active' ? '#378A921A' : '#F5A5241A',
            color: status === 'active' ? '#378A92' : '#F5A524',
          }}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Chip>
      </div>
    </div>
  )
}

const Users = () => {
  const { showPaymentOverdueBanner } = useBillingStatusStore()
  const isMobile = useIsMobile()
  const location = useLocation()
  const navigate = useNavigate()
  const { tenantId } = useParams<{ tenantId?: string }>()
  const { user } = useUserStore()
  const queryClient = useQueryClient()

  // Bulk-invite history is tenant-wide and now open to super admins too (the list
  // endpoint scopes by the x-tenant-id header, which the axios interceptor sends on
  // every request). This is what finally makes super-admin-initiated uploads
  // visible — previously they were written to the schema but nobody could list them.
  const showBulkHistory =
    user?.userRole === UserRoleEnum.TENANT_ADMIN || user?.userRole === UserRoleEnum.SUPER_ADMIN

  // State to track when tenant header is properly set
  const [isTenantHeaderReady, setIsTenantHeaderReady] = useState(false)

  const { data: quotaTenantSummary, isLoading: isTenantSummaryLoading } = useTenantQuotaSummary({
    tenantStatus: user?.tenant?.status,
    tenantId: tenantId,
    enabled: !tenantId || isTenantHeaderReady, // Only enable if no tenantId or header is ready
  })

  // URL parameter management
  const {
    values: urlParams,
    updateParam,
    updateParams,
  } = useURLParams({
    page: { key: 'page', defaultValue: 1 },
    rowsPerPage: { key: 'rowsPerPage', defaultValue: 10 },
    search: { key: 'search', defaultValue: '' },
  })

  const { page, rowsPerPage, search } = urlParams

  // Check if this is a tenant-specific view (Super Admin accessing specific tenant)
  const isTenantSpecificView =
    !!tenantId &&
    (user?.userRole === UserRoleEnum.SUPER_ADMIN || user?.userRole === UserRoleEnum.RESELLER_ADMIN)

  // Get tenant information for tenant-specific view
  const { data: tenantData, isLoading: isTenantLoading } = useTenant(tenantId || '')

  // Get own tenant details when not in tenant-specific view
  const { data: myTenantData, isLoading: isMyTenantLoading } = useMyTenantDetails({
    enabled: !tenantId && user?.userRole === UserRoleEnum.TENANT_ADMIN,
  })

  // Use appropriate tenant data based on view context
  const currentTenantData = isTenantSpecificView ? tenantData : myTenantData
  const isCurrentTenantLoading = isTenantSpecificView ? isTenantLoading : isMyTenantLoading

  // Track if we've already cleared the parameters to prevent repeated clearing
  const hasClearedParams = useRef(false)

  // Check if we should show the not found error state
  const showNotFoundError = location.state?.showNotFoundError
  const hasShownErrorToast = useRef(false)

  // No longer using location state for return state

  // Show toast notification when redirected due to 404 error
  useEffect(() => {
    if (showNotFoundError && !hasShownErrorToast.current) {
      hasShownErrorToast.current = true
      toast.error('Page not found', {
        description: "The page or resource you're looking for doesn't exist",
      })
    }
  }, [showNotFoundError])

  // No longer needed - state restoration is handled by URL parameters

  // Update tenant ID header for tenant-specific view and clear query params
  useEffect(() => {
    if (isTenantSpecificView && tenantId) {
      updateTenantIdHeader(tenantId)
      // Set header as ready after updating
      setIsTenantHeaderReady(true)

      // Clear query parameters when entering tenant-specific view (only once)
      // This prevents old query params from tenant management from interfering
      if (!hasClearedParams.current && !location.state?.fromTenantManagement) {
        const currentUrl = new URL(window.location.href)

        if (currentUrl.search) {
          const newUrl = currentUrl.pathname

          window.history.replaceState({}, document.title, newUrl)

          // Reset URL parameters to default values
          updateParams({
            page: 1,
            rowsPerPage: 10,
            search: '',
          })
        }

        // Mark that we've cleared the parameters
        hasClearedParams.current = true
      }
    } else {
      resetTenantIdHeader()
      // Reset the flag when not in tenant-specific view
      hasClearedParams.current = false
      // Header is ready when not in tenant-specific view (no tenantId needed)
      setIsTenantHeaderReady(true)
    }

    // Cleanup: reset header when component unmounts or tenant changes
    return () => {
      if (isTenantSpecificView) {
        resetTenantIdHeader()
      }
    }
  }, [isTenantSpecificView, tenantId, location.state?.fromTenantManagement, updateParams])

  const debouncedSearch = useDebounce(search, 500)

  const offset = useMemo(() => (page - 1) * rowsPerPage, [page, rowsPerPage])

  const filters = useMemo(
    () => ({
      limit: rowsPerPage,
      offset: offset,
      search: debouncedSearch || undefined,
      tenantId: tenantId,
    }),
    [rowsPerPage, offset, debouncedSearch, tenantId],
  )

  // Disable API calls for TENANT_USER role
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
    error: usersError,
    refetch,
  } = useGetUsers(filters, {
    enabled: user?.userRole !== UserRoleEnum.TENANT_USER,
    tenantStatus: user?.tenant?.status,
  })

  // Use the page focus refetch hook to automatically refetch data
  usePageFocusRefetch(() => {
    if (user?.userRole !== UserRoleEnum.TENANT_USER) {
      refetch()
    }
  })

  const { mutateAsync: mutateAsyncDeleteUser } = useDeleteUserInvitation()
  const { mutateAsync: mutateAsyncResendUser } = useResendUserInvitation()
  const { mutateAsync: mutateAsyncSuspendUser } = useSuspendUser()
  const { mutateAsync: mutateAsyncActivateUser } = useActivateUser()

  const handlePageChange = (newPage: number) => {
    updateParam('page', newPage, false)
  }

  const handleRowsPerPageChange = (newLimit: number) => {
    updateParam('rowsPerPage', newLimit)
  }

  const handleSearchChange = (newSearch: string) => {
    updateParam('search', newSearch)
  }

  const users = usersData?.data || []

  const pageInfo = usersData?.pageInfo || { totalPages: 1, count: 0 }

  const totalPages = pageInfo.totalPages || 1

  // Read isPrimary from the signed-in user (/users/me), never from `users`: that list is a
  // single server-paginated, search-filtered page, so looking ourselves up in it silently
  // fails whenever our own row isn't on the page being viewed.
  const currentUserIsPrimary =
    user?.isPrimary ||
    user?.userRole === UserRoleEnum.SUPER_ADMIN ||
    user?.userRole === UserRoleEnum.RESELLER_ADMIN

  const handleAddEditUser = (user: UserDataResponse, action: string) => {
    const config = {
      modalHeader: {
        title: action === 'edit' ? 'Edit User' : 'Invite New User',
        subtitle:
          action === 'edit'
            ? 'Please fill the form to edit the User.'
            : 'Your user will receive an invitation to their email.',
        showCloseButton: true,
      },
      data: {
        ...user,
        quotaTenantSummary: quotaTenantSummary as TenantQuotaSummaryDto | undefined,
        tenantId,
        currentUserIsPrimary,
      },
      action: action,
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[490px]',
      modelActive: ModelControlEnum.ADD_USER,
      toggleModel: true,
      scrollBehavior: 'outside',
    })
  }

  const handleBulkInvite = () => {
    useActiveModelStore.setState({
      data: {
        modalHeader: {
          title: 'Bulk Invite Users',
          subtitle: 'Upload a CSV or Excel file to invite many users at once.',
          showCloseButton: true,
          showExpandButton: true,
        },
        data: { tenantId, showHistory: showBulkHistory },
      },
      // Wider when the History tab (with its table) is present.
      width: showBulkHistory ? 'max-w-4xl' : 'max-w-[680px]',
      modelActive: ModelControlEnum.BULK_INVITE,
      toggleModel: true,
      scrollBehavior: 'outside',
    })
  }

  const handleDeleteUser = async (userId: string) => {
    // Find the user to get their status and details
    const userToDelete = users.find(u => u.id === userId)

    if (!userToDelete) {
      toast.error('User not found')

      return
    }

    const userStatus = userToDelete.status || (userToDelete.isActive ? 'active' : 'disabled')

    // Handle different user statuses according to requirements
    if (userStatus === 'invited' || userStatus === 'disabled') {
      // Direct deletion for invited users (not accepted invitation)
      try {
        await mutateAsyncDeleteUser(userId, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.quota.tenantSummary(tenantId) })
          },
        })
        toast.success('User invitation deleted successfully')
      } catch (error) {
        console.error('Failed to delete user invitation:', error)
        toast.error('Failed to delete user invitation')
      }
    } else if (userStatus === 'suspended') {
      // Show confirmation modal for suspended users
      const config = {
        modalHeader: {
          title: 'Delete User',
          showCloseButton: true,
        },
        data: {
          user: userToDelete,
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
            queryClient.invalidateQueries({ queryKey: queryKeys.quota.tenantSummary(tenantId) })
          },
        },
      }

      useActiveModelStore.setState({
        data: config,
        width: 'max-w-[500px]',
        modelActive: ModelControlEnum.DELETE_USER_CONFIRMATION,
        toggleModel: true,
        scrollBehavior: 'outside',
      })
    } else {
      // Active users cannot be deleted directly - they must be suspended first
      toast.error('Cannot delete active user', {
        description: 'Please suspend the user first before deletion.',
      })
    }
  }

  const handleSuspendUser = async (userId: string) => {
    const targetUser = users.find(u => u.id === userId)

    // Suspending an admin is reserved for super admins, whatever the tenant admin's standing.
    const isTenantAdmin = user?.userRole === UserRoleEnum.TENANT_ADMIN
    const isTargetTenantAdmin = targetUser?.userRole === UserRoleEnum.TENANT_ADMIN

    if (isTenantAdmin && isTargetTenantAdmin) {
      toast.error('Permission Denied', {
        description: 'Tenant Admin cannot suspend other Tenant Admins',
      })

      return
    }

    try {
      await mutateAsyncSuspendUser(userId)
      toast.success('User suspended successfully')
      // Refetch users after suspension
    } catch (error: any) {
      console.error('Failed to suspend user:', error)
      toast.error('Failed to suspend user', {
        description:
          error?.response?.data?.message || 'An error occurred while suspending the user',
      })
    }
  }

  const handleResendInvitation = async (userId: string) => {
    try {
      await mutateAsyncResendUser(userId)
      toast.success('Invitation sent successfully')
      // Show success message
    } catch (error: any) {
      toast.error('Failed to resend invitation', {
        description: error?.message || 'An error occurred while resending the invitation',
      })
      console.error('Failed to resend invitation:', error)
    }
  }

  const handleActivateUser = async (userId: string) => {
    try {
      await mutateAsyncActivateUser(userId)
      toast.success('User activated successfully')
    } catch (error) {
      console.error('Failed to activate user:', error)
    }
  }

  const handleTransferAdmin = (targetUser: UserDataResponse) => {
    // Get list of tenant admins excluding the current primary admin
    const tenantAdmins = users.filter(
      u => u.userRole === UserRoleEnum.TENANT_ADMIN && !u.isPrimary && u.status === 'active',
    )

    const config = {
      modalHeader: {
        title: 'Transfer Primary Admin Role',
        showCloseButton: true,
      },
      data: {
        targetUser,
        availableAdmins: tenantAdmins,
      },
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[550px]',
      modelActive: ModelControlEnum.TRANSFER_ADMIN,
      toggleModel: true,
      scrollBehavior: 'outside',
    })
  }

  const renderCell = (data: any, columnKey: Column['uid']) => {
    const users = data
    const status = users.status || (users.isActive ? 'active' : 'disabled')

    switch (columnKey) {
      case 'user':
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#E15F781A] text-gray-900 dark:text-white  flex items-center justify-center text-base font-bold uppercase">
              {users.firstName
                ?.split(' ')
                .map((n: string) => n[0])
                .join('')
                .slice(0, 2)}
              {users.lastName
                ?.split(' ')
                .map((n: string) => n[0])
                .join('')
                .slice(0, 2)}
            </div>
            <div className="flex flex-col gap-1 font-light text-sm text-gray-900 dark:text-white">
              <div className="flex items-center gap-2">
                <p className="text-base font-medium">
                  {users.firstName} {users.lastName}
                </p>
                {users.id === user?.id && (
                  <Chip color="primary" size="sm" variant="flat">
                    Me
                  </Chip>
                )}
                {users.isPrimary && (
                  <Chip color="warning" size="sm" variant="flat">
                    Primary Admin
                  </Chip>
                )}
                <Chip
                  color={users?.userRole === 'TENANT_ADMIN' ? 'success' : 'warning'}
                  size="sm"
                  variant="flat"
                >
                  {users?.userRole === 'TENANT_ADMIN'
                    ? 'Admin'
                    : users?.userRole === 'TENANT_USER'
                      ? 'User'
                      : users?.userRole || 'N/A'}
                </Chip>
              </div>
              <p className="text-sm">{users.email}</p>
            </div>
          </div>
        )

      case 'apiUsage': {
        const apiUsage = formatApiUsage(users)

        return (
          <div className="flex flex-col gap-1 text-gray-900 dark:text-white">
            {apiUsage.displayText}
          </div>
        )
      }

      case 'qpaSeedCount': {
        const seeds = (users as any).quotaSummary?.qpa?.quota ?? (users as any).qpaSeedCount ?? 0
        const hasQpa = seeds > 0

        return (
          <Chip
            size="sm"
            variant="flat"
            classNames={{
              base: hasQpa
                ? 'bg-violet-100 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700'
                : 'bg-gray-100 dark:bg-[#2A2A2A] border border-gray-200 dark:border-[#3A3A3A]',
              content: hasQpa
                ? 'text-violet-700 dark:text-violet-300 font-medium'
                : 'text-gray-400 dark:text-[#707070]',
            }}
          >
            {hasQpa ? `Assigned` : 'No access'}
          </Chip>
        )
      }

      case 'storageUsage': {
        const storageUsage = formatStorageUsage(users)

        return (
          <div className="flex flex-col gap-1 text-gray-900 dark:text-white">
            <p className="text-sm font-medium">{storageUsage.displayText}</p>
            <div className="w-[148px] dark:bg-[#2A2A2A] bg-gray-200 rounded-full h-2 overflow-visible relative">
              <div
                className={`${storageUsage.overQuota ? 'bg-red-600' : 'bg-blue-600 dark:bg-blue-400'} h-2 rounded-full transition-all duration-300`}
                style={{ width: `${Math.min(storageUsage.percentage, 100)}%` }}
              />
              {storageUsage.percentage > 100 && (
                <div
                  className={`${storageUsage.overQuota ? 'bg-red-600' : 'bg-blue-600 dark:bg-blue-400'} h-2 rounded-full opacity-50 absolute top-0 right-0`}
                  style={{ width: `${Math.min(storageUsage.percentage - 100, 20)}%` }}
                />
              )}
            </div>
            <p className="text-xs text-gray-900 dark:text-white">
              {storageUsage.total === 0 && storageUsage.used > 0
                ? 'Over quota - No quota allocated'
                : `${storageUsage.percentage}% used`}
            </p>
          </div>
        )
      }

      case 'status': {
        const status = users.status || (users.isActive ? 'active' : 'disabled')

        return (
          <Chip
            className="capitalize rounded font-medium"
            size="sm"
            variant="flat"
            style={{
              backgroundColor: status === 'active' ? '#378A921A' : '#F5A5241A',
              color: status === 'active' ? '#378A92' : '#F5A524',
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Chip>
        )
      }
      case 'lastActive':
        // Since API doesn't provide lastActive, show creation date or placeholder
        return (
          <span className="text-gray-900 dark:text-white">
            {users.lastLoginAt ? moment(users.lastLoginAt).format('MM/DD/YYYY') : 'N/A'}
          </span>
        )

      case 'actions': {
        // Only a super admin may suspend an admin. Tenant admins — primary or not — are limited
        // to suspending tenant users. Mirrors the guard in handleSuspendUser.
        const isSuperAdmin =
          user?.userRole === UserRoleEnum.SUPER_ADMIN ||
          user?.userRole === UserRoleEnum.RESELLER_ADMIN
        const isTargetTenantAdmin = users.userRole === UserRoleEnum.TENANT_ADMIN
        const canSuspend =
          status === 'active' && users.id !== user?.id && (isSuperAdmin || !isTargetTenantAdmin)

        return (
          <div className="flex items-center gap-2">
            <Dropdown
              isDisabled={isTenantSummaryLoading || isUsersFetching}
              classNames={{
                content: 'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
              }}
            >
              <DropdownTrigger>
                <Button isIconOnly size="sm" variant="light">
                  <span className="text-2xl">⋮</span>
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownItem key="edit" onPress={() => handleAddEditUser(users, 'edit')}>
                  Edit User
                </DropdownItem>
                {canSuspend ? (
                  <DropdownItem key="suspend" onPress={() => handleSuspendUser(users.id)}>
                    Suspend User
                  </DropdownItem>
                ) : null}

                {status === 'suspended' ? (
                  <DropdownItem key="enable" onPress={() => handleActivateUser(users.id)}>
                    Enable
                  </DropdownItem>
                ) : null}

                {status !== 'active' && status !== 'suspended' && users.id !== user?.id ? (
                  <DropdownItem key="resend" onPress={() => handleResendInvitation(users.id)}>
                    Resend Invitation
                  </DropdownItem>
                ) : null}

                {(currentUserIsPrimary ||
                  user?.userRole === UserRoleEnum.SUPER_ADMIN ||
                  user?.userRole === UserRoleEnum.RESELLER_ADMIN) &&
                users.userRole === UserRoleEnum.TENANT_ADMIN &&
                !users.isPrimary &&
                status === 'active' ? (
                  <DropdownItem key="transfer" onPress={() => handleTransferAdmin(users)}>
                    Transfer Admin
                  </DropdownItem>
                ) : null}

                {/* Delete User - Available for invited/suspended users, not for active users or current user */}
                {users.id !== user?.id &&
                (status === 'invited' || status === 'suspended') &&
                (users.userRole !== UserRoleEnum.TENANT_ADMIN ||
                  user?.userRole === UserRoleEnum.SUPER_ADMIN ||
                  user?.userRole === UserRoleEnum.RESELLER_ADMIN) &&
                !users.isPrimary ? (
                  <DropdownItem key="delete" onPress={() => handleDeleteUser(users.id)}>
                    Delete User
                  </DropdownItem>
                ) : null}
              </DropdownMenu>
            </Dropdown>
          </div>
        )
      }

      default:
        return <span className="text-gray-900 dark:text-white">{data[columnKey]}</span>
    }
  }

  if (usersError) {
    return <ErrorData description="Please try again later." title="Error Loading Users" />
  }

  // Show loading state when tenant data is loading
  if (isTenantSpecificView && isTenantLoading) {
    return (
      <section className="md:px-[30px] md:pb-0 px-4 flex flex-col gap-5">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-[#AFAFAF]">Loading tenant information...</p>
          </div>
        </div>
      </section>
    )
  }

  const handleBackToTenants = () => {
    // Get return state from current URL
    const urlParams = new URLSearchParams(window.location.search)
    const returnState = urlParams.get('returnState')

    // Build return URL with preserved state
    const returnUrl = new URL('/dashboard/management/tenants', window.location.origin)

    if (returnState) {
      returnUrl.searchParams.set('returnState', returnState)
    }

    navigate(returnUrl.pathname + returnUrl.search)
  }

  return (
    <section className="md:px-[30px] md:pb-0 px-4 flex flex-col gap-5">
      {/* Breadcrumb Navigation */}
      {isTenantSpecificView && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
          <button className="hover:text-primary transition-colors" onClick={handleBackToTenants}>
            Tenant Management
          </button>
          <span>{'>'}</span>
          <span
            className="text-gray-900 dark:text-white truncate max-w-50"
            title={(currentTenantData?.name || 'Tenant') + ' Storage & API Quota'}
          >
            {currentTenantData?.name || 'Tenant'} Storage & API Quota
          </span>
        </div>
      )}

      {/* Tenant Information Header */}
      {isCurrentTenantLoading ? (
        <TenantHeaderSkeleton />
      ) : (
        currentTenantData && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Account Card */}
            <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-3 lg:p-4 shadow">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-[#2A2A2A] pb-3 mb-4">
                Account
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">Name:</span>{' '}
                    {currentTenantData.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">Type:</span>{' '}
                    {currentTenantData.tenantType}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">Status:</span>{' '}
                    <Chip
                      className="capitalize rounded font-medium"
                      size="sm"
                      variant="flat"
                      style={{
                        backgroundColor:
                          currentTenantData.status === 'active' ? '#378A921A' : '#F5A5241A',
                        color: currentTenantData.status === 'active' ? '#378A92' : '#F5A524',
                      }}
                    >
                      {currentTenantData.status.charAt(0).toUpperCase() +
                        currentTenantData.status.slice(1)}
                    </Chip>
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">Registered:</span>{' '}
                    {new Date(currentTenantData.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Admin Contact Card */}
            <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-3 lg:p-4 shadow">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-[#2A2A2A] pb-3 mb-4">
                Admin Contact
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">Name:</span>{' '}
                    {currentTenantData.firstName} {currentTenantData.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">Email:</span>{' '}
                    {currentTenantData.adminEmail}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">Phone:</span>{' '}
                    {currentTenantData.phoneNumber || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Plan DetailsCard */}
            <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-3 lg:p-4 shadow">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-[#2A2A2A] pb-3 mb-4">
                Plan Details
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">Plan:</span>{' '}
                    {currentTenantData.plan?.name ? currentTenantData.plan?.name : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">Next Billing:</span>{' '}
                    {currentTenantData.nextBillingCycleDate
                      ? new Date(currentTenantData.nextBillingCycleDate).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">
                      Storage Limit:
                    </span>{' '}
                    {quotaTenantSummary?.storage
                      ? formatStorageUsageUtil(
                          quotaTenantSummary.storage.used,
                          quotaTenantSummary.storage.quota,
                        )
                      : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">
                      Storage Allocated:
                    </span>{' '}
                    {quotaTenantSummary?.storage
                      ? formatStorageValue(quotaTenantSummary.storage.allocated || 0)
                      : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">QPA License:</span>{' '}
                    {quotaTenantSummary?.qpa
                      ? `${quotaTenantSummary.qpa.used.toLocaleString()}/${quotaTenantSummary.qpa.quota.toLocaleString()} assigned`
                      : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">API Limit:</span>{' '}
                    {quotaTenantSummary?.service
                      ? `${quotaTenantSummary.service.used.toLocaleString()}/${quotaTenantSummary.service.quota.toLocaleString()} calls/month`
                      : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 dark:text-[#AFAFAF] break-all">
                    <span className="text-gray-900 dark:text-white font-medium">
                      API Allocated:
                    </span>{' '}
                    {quotaTenantSummary?.service
                      ? quotaTenantSummary.service.allocated.toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      <div className=" flex justify-between flex-wrap items-center gap-4">
        <div>
          <h1 className="font-medium text-2xl">
            {isTenantSpecificView ? 'Storage & API Quota Management' : 'Manage Storage & API Quota'}
          </h1>
          <p>
            {isTenantSpecificView
              ? `Manage storage and API quota for ${currentTenantData?.name || 'this tenant'}`
              : 'Manage storage and API quota for your users'}
          </p>
        </div>
        <div className="lg:flex items-center gap-3 hidden">
          <div className="max-w-[278px]">
            <Input
              isClearable
              maxLength={256}
              placeholder="Find anything"
              radius="sm"
              size="lg"
              value={search}
              variant="bordered"
              classNames={{
                label: [
                  'text-foreground',
                  'dark:text-foreground',
                  'group-data-[filled-within=true]:text-[#000]',
                  'dark:group-data-[filled-within=true]:text-white',
                  'group-data-[invalid=true]:text-danger',
                  'dark:group-data-[invalid=true]:text-danger',
                ],
                input: [
                  'bg-transparent w-[278px] !pr-32',
                  'dark:text-white text-black',
                  'placeholder:text-[#94A3B8] dark:placeholder:text-white/60 group-data-[invalid=true]:placeholder:text-danger',
                ],
                innerWrapper: 'bg-transparent',
                inputWrapper: [
                  'dark:bg-[#1e1e20] group-data-[focus=true]:!border-[#000] dark:group-data-[focus=true]:!border-[#fff]',
                  'dark:hover:bg-[#1e1e20]/70',
                  'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                  '!cursor-text',
                ],
              }}
              endContent={
                <div className="flex items-center">
                  <span
                    className={cn(
                      'text-xs text-gray-500 w-10',
                      search.length == 0 ? 'opacity-0' : 'opacity-100',
                    )}
                  >
                    {search.length}/256
                  </span>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => handleSearchChange('')}
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>
              }
              startContent={
                <img alt="search" className="dark:invert" src="/assets/svg/search.svg" />
              }
              onClear={() => handleSearchChange('')}
              onValueChange={handleSearchChange}
            />
          </div>
          {user?.userRole !== UserRoleEnum.TENANT_USER && (
            <>
              <Button radius="sm" size="lg" variant="bordered" onPress={handleBulkInvite}>
                Bulk Invite
              </Button>
              <Button
                color="primary"
                radius="sm"
                size="lg"
                onPress={() => {
                  handleAddEditUser({} as UserDataResponse, 'add')
                  // Note: handleUserCreationSuccess will be called from the modal component after successful creation
                }}
              >
                New User
              </Button>
            </>
          )}
        </div>
      </div>

      {isMobile ? (
        <div className="flex flex-col gap-4">
          {/* Mobile Search and New User Button */}
          <div className="flex flex-col gap-3">
            <Input
              isClearable
              maxLength={256}
              placeholder="Find anything"
              radius="sm"
              size="lg"
              value={search}
              variant="bordered"
              classNames={{
                label: [
                  'text-foreground',
                  'dark:text-foreground',
                  'group-data-[filled-within=true]:text-[#000]',
                  'dark:group-data-[filled-within=true]:text-white',
                  'group-data-[invalid=true]:text-danger',
                  'dark:group-data-[invalid=true]:text-danger',
                ],
                input: [
                  'bg-transparent',
                  'dark:text-white text-black',
                  'placeholder:text-[#94A3B8] dark:placeholder:text-white/60 group-data-[invalid=true]:placeholder:text-danger',
                ],
                innerWrapper: 'bg-transparent',
                inputWrapper: [
                  'dark:bg-[#1e1e20] group-data-[focus=true]:!border-[#000] dark:group-data-[focus=true]:!border-[#fff]',
                  'dark:hover:bg-[#1e1e20]/70',
                  'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                  '!cursor-text',
                ],
              }}
              endContent={
                <div className="flex items-center">
                  <span
                    className={cn(
                      'text-xs text-gray-500 w-10',
                      search.length == 0 ? 'opacity-0' : 'opacity-100',
                    )}
                  >
                    {search.length}/256
                  </span>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => handleSearchChange('')}
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>
              }
              startContent={
                <img alt="search" className="dark:invert" src="/assets/svg/search.svg" />
              }
              onClear={() => handleSearchChange('')}
              onValueChange={handleSearchChange}
            />
            {user?.userRole !== UserRoleEnum.TENANT_USER && (
              <>
                <Button
                  className="w-full"
                  radius="sm"
                  variant="bordered"
                  onPress={handleBulkInvite}
                >
                  Bulk Invite
                </Button>
                <Button
                  className="w-full"
                  color="primary"
                  radius="sm"
                  onPress={() => {
                    handleAddEditUser({} as UserDataResponse, 'add')
                  }}
                >
                  New User
                </Button>
              </>
            )}
          </div>

          <ScrollShadow className="max-h-[calc(100vh-380px)] pb-10 scrollbar-hide">
            {isUsersLoading ? (
              <div className="grid grid-cols-1 gap-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <UserCardSkeleton key={index} />
                ))}
              </div>
            ) : users.length === 0 ? (
              <EmptyState
                className="min-h-[300px]"
                icon={<EmptyStateIcon className="text-gray-400 dark:text-gray-500" />}
                title="No users found"
                description={
                  debouncedSearch
                    ? 'No users match your search criteria. Try adjusting your search terms.'
                    : "You haven't invited any users yet. Click 'New User' to get started."
                }
              />
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {users.map(userItem => (
                  <UserCard
                    key={userItem.id}
                    currentUserId={user?.id}
                    currentUserIsPrimary={currentUserIsPrimary}
                    currentUserRole={user?.userRole}
                    user={
                      userItem as UserDataResponse & {
                        storageQuota?: number | null
                        apiQuota?: number | null
                        status?: string | undefined
                        quotaSummary?: QuotaSummaryDto | undefined
                      }
                    }
                    onActivate={handleActivateUser}
                    onDelete={handleDeleteUser}
                    onEdit={handleAddEditUser}
                    onResendInvitation={handleResendInvitation}
                    onSuspend={handleSuspendUser}
                    onTransferAdmin={handleTransferAdmin}
                  />
                ))}
              </div>
            )}
          </ScrollShadow>

          {users.length > 0 && (
            <div className="flex justify-between items-center mt-4 gap-2">
              <Dropdown
                size="sm"
                classNames={{
                  content:
                    'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
                }}
              >
                <DropdownTrigger>
                  <Button
                    endContent={<ChevronDownIcon className="text-small" />}
                    size="sm"
                    variant="light"
                  >
                    {rowsPerPage}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="Rows per page"
                  selectedKeys={new Set([rowsPerPage.toString()])}
                  selectionMode="single"
                  onAction={key => {
                    handleRowsPerPageChange(Number(key))
                  }}
                >
                  <DropdownItem key="10">10</DropdownItem>
                  <DropdownItem key="25">25</DropdownItem>
                  <DropdownItem key="100">100</DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          )}
        </div>
      ) : (
        <TableComponent
          key={isTenantSummaryLoading || isUsersFetching ? 'loading' : 'not-loading'}
          columns={columns}
          currentPage={page}
          customRenderCell={renderCell}
          data={users}
          disableLocalPagination={true}
          isLoading={isUsersLoading}
          rowsPerPage={rowsPerPage}
          selectionMode="none"
          showBottomContent={true}
          showTopContent={false}
          total={pageInfo.count || users.length}
          totalPages={totalPages}
          trClassName="border-b dark:border-[#2A2A2A] border-[#ddd]"
          high={
            isTenantSpecificView
              ? 'h-[calc(100vh-600px)]'
              : showPaymentOverdueBanner
                ? 'h-[calc(100vh-550px)]'
                : 'h-[calc(100vh-560px)]'
          }
          initialVisibleColumns={[
            'user',
            'apiUsage',
            'storageUsage',
            'status',
            // 'lastActive',
            'actions',
            'qpaSeedCount',
          ]}
          onLimitChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
        />
      )}
    </section>
  )
}

export default Users
